import { useMemo, useState, type FormEvent } from 'react'
import { Modal } from '../components/Modal'
import { PageLoader } from '../components/PageLoader'
import { ProductMultiSelect } from '../components/ProductMultiSelect'
import { Spinner } from '../components/Spinner'
import { StatusToggle } from '../components/StatusToggle'
import { Topbar } from '../components/Topbar'
import { LOADING_LABELS } from '../constants/loading'
import {
  EMPTY_PRODUCT_FORM,
  INVENTORY_PAGE,
  type ProductFormState,
  type VendorProduct,
} from '../constants/vendor'
import { ApiError } from '../helpers/api-client'
import {
  assignableCatalogProducts,
  catalogProductName,
  statusToggleLabel,
} from '../helpers/vendor'
import {
  createVendorProduct,
  deleteVendorProduct,
  updateVendorProduct,
} from '../helpers/vendor-api'
import { useAuth } from '../hooks/useAuth'
import { useVendorData } from '../hooks/useVendorData'
import '../styles/catalog-page.scss'
import '../styles/catalog-form.scss'
import '../styles/vendor-table.scss'

export function InventoryPage() {
  const { vendorId } = useAuth()
  const { location, products, catalog, loading, error, refresh } =
    useVendorData({ includeCatalog: true })
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<ProductFormState>(EMPTY_PRODUCT_FORM)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const assignable = useMemo(
    () => assignableCatalogProducts(catalog, products),
    [catalog, products],
  )

  function openCreate() {
    if (!location) return
    setForm(EMPTY_PRODUCT_FORM)
    setFormError('')
    setModalOpen(true)
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!vendorId || form.productIds.length === 0) {
      setFormError(INVENTORY_PAGE.required)
      return
    }
    setSaving(true)
    setFormError('')
    try {
      await createVendorProduct(vendorId, form)
      setModalOpen(false)
      await refresh()
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : INVENTORY_PAGE.saveError,
      )
    } finally {
      setSaving(false)
    }
  }

  async function patchProduct(
    item: VendorProduct,
    patch: Parameters<typeof updateVendorProduct>[1],
  ) {
    setBusyId(item.id)
    try {
      await updateVendorProduct(item.id, patch)
      await refresh()
    } catch {
      await refresh()
    } finally {
      setBusyId(null)
    }
  }

  async function onDelete(item: VendorProduct) {
    if (!window.confirm(INVENTORY_PAGE.confirmRemove)) return
    setBusyId(item.id)
    try {
      await deleteVendorProduct(item.id)
      await refresh()
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="catalog-page">
        <Topbar title={INVENTORY_PAGE.title} subtitle={INVENTORY_PAGE.subtitle} />
        <PageLoader />
      </div>
    )
  }

  return (
    <div className="catalog-page">
      <Topbar title={INVENTORY_PAGE.title} subtitle={INVENTORY_PAGE.subtitle} />
      {error ? (
        <section className="panel" style={{ padding: 16 }}>
          <p style={{ color: 'var(--danger)' }}>{error}</p>
        </section>
      ) : null}

      <section className="panel vendor-panel">
        <div className="panel__header">
          <h2 className="panel__title">{INVENTORY_PAGE.listTitle}</h2>
          <button
            type="button"
            className="btn btn--create"
            onClick={openCreate}
            disabled={!location}
          >
            {INVENTORY_PAGE.add}
          </button>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{INVENTORY_PAGE.colProduct}</th>
                <th>{INVENTORY_PAGE.colPrep}</th>
                <th>{INVENTORY_PAGE.colStatus}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={4} className="table-empty">
                    {!location
                      ? INVENTORY_PAGE.emptyLocations
                      : INVENTORY_PAGE.empty}
                  </td>
                </tr>
              ) : (
                products.map((item) => {
                  const name = catalogProductName(catalog, item.productId)
                  return (
                    <tr key={item.id}>
                      <td className="vendor-cell__name">{name}</td>
                      <td>
                        <input
                          type="number"
                          min={1}
                          className="inline-number"
                          defaultValue={item.prepTimeMinutes}
                          key={`${item.id}-prep-${item.prepTimeMinutes}`}
                          disabled={busyId === item.id}
                          onBlur={(event) => {
                            const next = Number(event.target.value)
                            if (
                              !Number.isFinite(next) ||
                              next < 1 ||
                              next === item.prepTimeMinutes
                            ) {
                              return
                            }
                            void patchProduct(item, { prepTimeMinutes: next })
                          }}
                        />
                      </td>
                      <td>
                        <StatusToggle
                          checked={item.isActive}
                          label={statusToggleLabel(name)}
                          busy={busyId === item.id}
                          onChange={() => {
                            void patchProduct(item, { isActive: !item.isActive })
                          }}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="icon-btn icon-btn--sm"
                          title={INVENTORY_PAGE.remove}
                          aria-label={INVENTORY_PAGE.remove}
                          disabled={busyId === item.id}
                          onClick={() => {
                            void onDelete(item)
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            width="16"
                            height="16"
                          >
                            <path d="M4 7h16" />
                            <path d="M9 7V5h6v2" />
                            <path d="M7 7l1 12h8l1-12" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        title={INVENTORY_PAGE.modalTitle}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        wide
        closeDisabled={saving}
      >
        <form className="catalog-form" onSubmit={onSubmit} aria-busy={saving}>
          <div className="catalog-form__grid">
            <div className="form-field form-field--wide">
              {assignable.length || form.productIds.length ? (
                <ProductMultiSelect
                  products={assignable}
                  selectedIds={form.productIds}
                  disabled={saving}
                  onChange={(productIds) =>
                    setForm((current) => ({ ...current, productIds }))
                  }
                />
              ) : (
                <p className="form-field__hint">{INVENTORY_PAGE.allAssigned}</p>
              )}
            </div>

            <label className="form-field">
              <span className="form-field__label">{INVENTORY_PAGE.prepLabel}</span>
              <input
                type="number"
                min={1}
                value={form.prepTimeMinutes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    prepTimeMinutes: event.target.value,
                  }))
                }
              />
            </label>

            <label className="form-check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isActive: event.target.checked,
                  }))
                }
              />
              {INVENTORY_PAGE.activeLabel}
            </label>
          </div>

          {formError ? (
            <p className="login-page__error" role="alert">
              {formError}
            </p>
          ) : null}

          <div className="catalog-form__footer">
            <button
              type="button"
              className="btn btn--outline"
              disabled={saving}
              onClick={() => setModalOpen(false)}
            >
              {INVENTORY_PAGE.cancel}
            </button>
            <button
              type="submit"
              className="btn btn--create"
              disabled={saving || form.productIds.length === 0}
              aria-busy={saving}
            >
              {saving ? (
                <Spinner inline label={LOADING_LABELS.saving} />
              ) : (
                INVENTORY_PAGE.save
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
